/**
 * The wire shapes of the platform-owner **account administration** screen (#279 completing #282).
 *
 * One listing endpoint and one destructive action, both `PlatformOwner`-only, both on `AccountController`'s
 * platform admin surface (`/auth/account/admin/…`). The screen exists so an operator can answer "who has a
 * second factor here, and at which provider" and then take one away for somebody who has lost both their phone
 * and their recovery codes.
 */

/**
 * The identity providers a stored account can have come from — the `ExternalIdentityProvider` names, as the
 * server writes them.
 *
 * `Entra` is dead (the broker that minted those rows is gone) but not removable: the enum is persisted as an
 * `int` on the server, so the member survives and old rows still carry it. It is offered here because an
 * operator looking at an old installation will find such rows and needs to be able to filter for them.
 */
export const ACCOUNT_IDENTITY_PROVIDERS = ['Local', 'Google', 'Microsoft', 'Apple', 'Entra'] as const;

export type AccountIdentityProvider = typeof ACCOUNT_IDENTITY_PROVIDERS[number];

/** One row of the listing — an account, plus the two facts the screen is about. */
export interface AccountIdentity {
  /** The `ExternalIdentity` id. The argument of the reset endpoint. */
  id: string;
  /** An {@link AccountIdentityProvider} name. Typed as `string` because the server owns the vocabulary. */
  provider: string;
  email: string | null;
  displayName: string | null;
  /** Whether a **confirmed** authenticator stands beside this account's password. */
  twoFactorEnabled: boolean;
  /** How many active accounts this identity opens — bracketed users plus employee links. */
  linkedAccounts: number;
  createdUtc: string;
  /**
   * The most recent successful sign-in — or `0001-01-01T00:00:00` for an account that has never signed in.
   *
   * The server's field is not nullable, so "never" arrives as .NET's `default(DateTime)`. Rendering that
   * verbatim would print a date in the year 1; {@link hasSignedIn} is the one place that is decided.
   */
  lastLoginUtc: string;
  /** `false` for a deactivated account, which is refused at sign-in but keeps everything it had. */
  active: boolean;
}

/** The narrowing sent as query parameters. Every field is optional; a blank one is omitted from the request. */
export interface AccountIdentityFilter {
  provider?: string | null;
  email?: string | null;
  name?: string | null;
  /** `true`/`false` narrow to accounts with/without a confirmed factor; `null`/absent means both. */
  twoFactor?: boolean | null;
}

/**
 * Whether this account has ever signed in.
 *
 * `.NET`'s `default(DateTime)` is `0001-01-01`, which `new Date(...)` parses happily into a real year-1 date —
 * so a date pipe would print "01.01.01" and read as a genuine (if odd) sign-in rather than as "never". Compared
 * against a year rather than the literal string because the server may serialise the offset (`+00:00`) or not,
 * and a string comparison would then be right only by luck.
 */
export function hasSignedIn(identity: AccountIdentity): boolean {
  const parsed = new Date(identity.lastLoginUtc);
  return !Number.isNaN(parsed.getTime()) && parsed.getUTCFullYear() > 1;
}

/**
 * The German label for a provider.
 *
 * `Local` is deliberately **not** "Lokal": from the operator's side the distinction that matters is *how the
 * person signs in*, and a native account signs in with an address and a password stored here — which is what a
 * second factor stands beside, and therefore the only kind of row on this screen where the 2FA column can ever
 * say yes. An unknown value is passed through rather than mapped to "Unbekannt": the server owns this
 * vocabulary, and a provider added there should show its own name here until somebody translates it.
 */
export function providerLabel(provider: string): string {
  switch (provider) {
    case 'Local':
      return 'E-Mail & Passwort';
    case 'Google':
      return 'Google';
    case 'Microsoft':
      return 'Microsoft';
    case 'Apple':
      return 'Apple';
    case 'Entra':
      return 'Entra (stillgelegt)';
    default:
      return provider;
  }
}

/**
 * What to call an account on screen: its display name, else its address, else its id.
 *
 * All three fall back rather than one of them being required, because every one of them is genuinely optional
 * on the server side — Apple reports a name exactly once and never again, and a native account may have been
 * created from an invitation that carried no name at all. The id is not decoration in that last case: it is
 * what the operator needs to identify the row they are about to act on.
 */
export function accountName(identity: AccountIdentity): string {
  return identity.displayName?.trim() || identity.email?.trim() || identity.id;
}
